import * as Tone from "tone";
import { chordToMidi, midiToNote, parseChordToken } from "./romanParser";
import { Register, voiceChordInRegister, midiToNote as registerMidiToNote } from "./registerVoicing";

export const progressions: {
  [key: string]: {
    roman: string[];
    mode: "minor" | "major";
    defaultRegister?: { bottomNote: string; topNote: string };
  };
} = {
  // minor progressions
  melancholic: {
    roman: ["i", "v", "bVI", "iv"], // Melancholic
    mode: "minor",
  },
  tense: {
    roman: ["i", "v", "bVI", "v"], // Tense
    mode: "minor",
  },
  sad: {
    roman: ["i", "iv", "v", "iv"], // Sad
    mode: "minor",
  },
  dramatic: {
    roman: ["i", "iv", "bVI", "v"], // Dramatic
    mode: "minor",
  },
  anxious: {
    roman: ["i", "bVI", "ii°", "v"], // Anxious
    mode: "minor",
  },
  emotional: {
    roman: ["i", "bVI", "iv", "v"], // Emotional
    mode: "minor",
  },
  somber: {
    roman: ["iv", "i", "v", "v"], // Somber
    mode: "minor",
  },
  tragic: {
    roman: ["i", "bVI", "i", "v"], // Tragic
    mode: "minor",
  },
  // major progressions
  hopeful: {
    roman: ["I", "V", "vi", "IV"],
    mode: "major",
  },
  yearning: {
    roman: ["I", "V", "vi", "V"],
    mode: "major",
  },
  nostalgic: {
    roman: ["I", "V", "ii", "IV"],
    mode: "major",
  },
  optimistic: {
    roman: ["I", "vi", "IV", "V"],
    mode: "major",
  },
  cheerful: {
    roman: ["I", "IV", "V", "IV"],
    mode: "major",
  },
  one: {
    roman: ["IVmaj7", "♯iv7", "iiadd9", "Imaj7"],
    mode: "major",
  },
  two: {
    roman: ["I", "♭VImaj7", "v7", "♭IImaj7"],
    mode: "major",
  },
  three: {
    roman: ["I9", "Imaj7/9", "I9", "II7#9"],
    mode: "major",
  },
  four: {
    roman: ["I7", "II7", "♭IIImaj7", "V9"],
    mode: "major",
  },
  five: {
    roman: ["I", "V", "♭VImaj7", "v7/♭7"],
    mode: "major",
  },
  six: {
    roman: ["vi", "IV", "vi", "v11"],
    mode: "major",
  },
  seven: {
    roman: ["Imaj7", "vii7", "♭VIImaj7", "iii7"],
    mode: "major",
  },
  eight: {
    roman: ["vi", "I", "II", "IV"],
    mode: "major",
  },
  nine: {
    roman: ["ii", "V", "♭VII", "Iadd9"],
    mode: "major",
  },
  ten: {
    roman: ["IV", "V", "vi", "iii"],
    mode: "major",
  },
  eleven: {
    roman: ["vi", "V", "iii", "IVmaj7"],
    mode: "major",
  },
  twelve: {
    roman: ["vi", "ii", "vi", "ii6"],
    mode: "major",
  },
  thirteen: {
    roman: ["IV", "I", "iii/7", "vi7"],
    mode: "major",
  },
  fourteen: {
    roman: ["Imaj7", "V/V", "♭VII13", "vi9"],
    mode: "major",
  },
  fifteen: {
    roman: ["vi7", "vii7", "Imaj7", "♯iidim7"],
    mode: "major",
  },
  sixteen: {
    roman: ["vi7", "♭VImaj7", "v7", "♭Vmaj7"],
    mode: "major",
  },
  seventeen: {
    roman: ["vi", "vii°11", "ii7/I", "IV6"],
    mode: "major",
  },
  eighteen: {
    roman: ["IV", "ii", "vi", "V"],
    mode: "major",
  },
};

/**
 * All 12 chromatic root notes
 */
export const ALL_ROOTS = [
  "C",
  "Db",
  "D",
  "Eb",
  "E",
  "F",
  "Gb",
  "G",
  "Ab",
  "A",
  "Bb",
  "B",
] as const;

/**
 * All supported chord extensions
 */
export type ChordExtension =
  | "maj"
  | "min"
  | "dim"
  | "aug"
  | "sus2"
  | "sus4"
  | "5"
  | "add6"
  | "add9"
  | "6"
  | "7"
  | "9"
  | "maj7"
  | "min6"
  | "min7"
  | "min9";

export const ALL_EXTENSIONS: ChordExtension[] = [
  "maj",
  "min",
  "dim",
  "aug",
  "sus2",
  "sus4",
  "5",
  "add6",
  "add9",
  "6",
  "7",
  "9",
  "maj7",
  "min6",
  "min7",
  "min9",
];

/**
 * Convert a note name (like "C", "F#") to its MIDI root number
 * MIDI note 60 = C4, so formula is: (octave + 1) * 12 + note
 */
export const noteNameToMidi = (
  noteName: string,
  octave: number = 4
): number => {
  const noteMap: { [key: string]: number } = {
    C: 0,
    "C#": 1,
    Db: 1,
    D: 2,
    "D#": 3,
    Eb: 3,
    E: 4,
    F: 5,
    "F#": 6,
    Gb: 6,
    G: 7,
    "G#": 8,
    Ab: 8,
    A: 9,
    "A#": 10,
    Bb: 10,
    B: 11,
  };

  return (octave + 1) * 12 + (noteMap[noteName] ?? 0);
};

/**
 * Parses a Roman numeral chord in a given key using our custom romanParser
 * @param roman Roman numeral (e.g., "I", "ii", "V7", "vi")
 * @param key Key signature (e.g., "C", "F#", "Bb")
 * @returns Object with chord information including notes
 */
export const parseChord = (
  key: string,
  mode: "minor" | "major",
  romanNumeral: string
): { notes: string[] } => {
  try {
    // Parse the Roman numeral using our custom parser
    const token = parseChordToken(romanNumeral);

    console.log("token", token);

    // Convert key name to MIDI root note
    const rootMidi = noteNameToMidi(key, 4);
    console.log("key", key);
    console.log("rootMidi", rootMidi);

    // Use the mode parameter to determine if we're in a minor key
    const minor = mode === "minor";

    // Get MIDI notes for the chord
    const midiNotes = chordToMidi(token, rootMidi, minor, true);

    // Convert MIDI notes back to note names (without octaves for now)
    const noteNames = midiNotes.map((midi) => {
      const noteName = midiToNote(midi);
      // Remove octave number to get just the note name
      return noteName.replace(/\d+$/, "");
    });

    return { notes: noteNames };
  } catch (error) {
    console.error(
      `Error parsing Roman chord ${romanNumeral} in ${key}:`,
      error
    );

    // Fallback to basic major triad in the key
    const rootMidi = noteNameToMidi(key, 4);
    const fallbackMidi = [rootMidi, rootMidi + 4, rootMidi + 7]; // Major triad
    const fallbackNotes = fallbackMidi.map((midi) =>
      midiToNote(midi).replace(/\d+$/, "")
    );

    return { notes: fallbackNotes };
  }
};

/**
 * Interface for the playRomanInKey function parameters
 */
export interface PlayRomanChordParams {
  roman: string;
  key: string;
  mode: "minor" | "major";
  baseOctave?: number; // Deprecated: use register instead
  duration?: Tone.Unit.Time;
  time?: Tone.Unit.Time;
  voicing?: number[]; // Deprecated: use register instead
  velocity?: number;
  strum?: number; // Strum percentage (0-1) that controls note timing offset
  register?: Register; // Register for voicing
  previousTopNote?: number; // Previous chord's top note for voice leading
}

export interface RomanChordNotesResult {
  notes: string[];
  topNote?: number;
  chordMidi: number[];
}

export const getRomanChordNotes = (
  params: Omit<PlayRomanChordParams, "duration" | "time" | "velocity" | "strum">
): RomanChordNotesResult => {
  const {
    roman,
    key,
    mode,
    baseOctave = 4,
    register,
    previousTopNote,
  } = params;

  const token = parseChordToken(roman);
  const rootMidi = noteNameToMidi(key, 4);
  const minor = mode === "minor";
  const chordMidi = chordToMidi(token, rootMidi, minor, true);

  let notesWithOctaves: string[];
  let topNote: number | undefined;

  if (register) {
    const voicedMidi = voiceChordInRegister(
      chordMidi,
      register,
      previousTopNote
    );
    notesWithOctaves = voicedMidi.map((midi) => registerMidiToNote(midi));
    topNote =
      voicedMidi.length > 0 ? voicedMidi[voicedMidi.length - 1] : undefined;
  } else {
    const chordInfo = parseChord(key, mode, roman);
    notesWithOctaves = chordInfo.notes.map((note) => `${note}${baseOctave}`);
  }

  return { notes: notesWithOctaves, topNote, chordMidi };
};

/**
 * Main function to play a Roman numeral chord in a given key using a sampler
 * @param sampler The Tone.js sampler instance
 * @param params Configuration object for the chord
 * @returns The top note MIDI number for voice leading, or undefined if no register voicing
 */
export const playRomanInKey = (
  sampler: Tone.Sampler,
  params: PlayRomanChordParams
): number | undefined => {
  const {
    roman,
    key,
    mode,
    baseOctave = 4,
    duration = "1m",
    time = "+0",
    velocity = 0.7,
    strum = 0, // Default to no strum
    register,
    previousTopNote,
  } = params;

  try {
    // Parse the Roman numeral chord using our new parser
    console.log("roman", roman);
    const { notes: notesWithOctaves, topNote, chordMidi } =
      getRomanChordNotes({
        roman,
        key,
        mode,
        baseOctave,
        register,
        previousTopNote,
      });

    // Enhanced chord logging
    const originalChordNotes = register
      ? chordMidi.map((midi) => registerMidiToNote(midi)).join(", ")
      : "N/A";

    console.log(`
🎵 CHORD PLAYED 🎵
Roman Numeral: ${roman}
Key: ${key}
Mode: ${mode}
${register ? `Original Chord MIDI: [${chordMidi.join(", ")}]` : ""}
${register ? `Original Chord Notes: [${originalChordNotes}]` : ""}
Final Notes (Voiced): [${notesWithOctaves.join(", ")}]
${register ? `Register: ${register.bottomNote} - ${register.topNote}` : `Base Octave: ${baseOctave}`}
${previousTopNote !== undefined ? `Previous Top Note: ${registerMidiToNote(previousTopNote)}` : ""}
${topNote !== undefined ? `Top Note: ${registerMidiToNote(topNote)}` : ""}
Duration: ${duration}
Velocity: ${velocity}
Time: ${time}
Strum: ${strum}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    `);

    // Calculate the max strum delay time in seconds based on strum percentage
    // Max strum of 1.0 will create a maximum delay of 120ms between notes
    const MAX_STRUM_DELAY = 0.12; // 120ms
    const strumDelay = strum * MAX_STRUM_DELAY;

    // If no strum effect or minimal strum (below threshold), try to play as a single chord
    if (strum < 0.05) {
      try {
        sampler.triggerAttackRelease(
          notesWithOctaves,
          duration,
          time,
          velocity
        );
        console.log(
          `✅ Chord played simultaneously: ${notesWithOctaves.join(", ")}`
        );
        return topNote;
      } catch (bufferError) {
        console.warn(
          "⚠️ Chord array failed, falling back to individual notes:",
          bufferError
        );
        // Continue to individual note playing logic below
      }
    }

    // Play notes individually with strum timing
    notesWithOctaves.forEach((note, index) => {
      // Calculate strum time offset for this note
      // Higher strum values will create larger gaps between notes
      const strumOffset = index * strumDelay;

      const noteTime =
        typeof time === "string"
          ? `+${strumOffset}` // Offset from now by strum timing
          : typeof time === "number"
          ? time + strumOffset
          : time;

      try {
        sampler.triggerAttackRelease(note, duration, noteTime, velocity);
        console.log(
          `🎼 Note played with strum: ${note} at time ${noteTime} (offset: ${strumOffset}s)`
        );
      } catch (noteError) {
        console.error(`❌ Failed to play note ${note}:`, noteError);
      }
    });
    console.log(
      `✅ All notes played with strum effect: ${notesWithOctaves.join(", ")}`
    );
    
    return topNote;
  } catch (error) {
    console.error("❌ Error playing Roman chord:", error);
    return undefined;
  }
};
